import { toZonedTime, fromZonedTime } from 'date-fns-tz';
import type { ScheduleEvent, CustomAttributeValue } from '../types';
import { durationMinutes } from './dateUtils';

/**
 * The Kendo data item shape we pass to / receive from the Scheduler.
 *
 * Important: we do NOT set Kendo's `timezone` prop. Instead, all Date values
 * stored here are "wall-clock" representations of the configured IANA timezone
 * (date-fns-tz's toZonedTime / fromZonedTime). This avoids the requirement to
 * load CLDR timezone data that Kendo otherwise needs.
 */
export interface KendoItem {
  id: string;
  title: string;
  /** Wall-clock Date in the configured timezone (NOT real UTC) */
  start: Date;
  /** Wall-clock Date in the configured timezone (NOT real UTC) */
  end: Date;
  startTimezone: null;
  endTimezone: null;
  isAllDay: boolean;
  recurrenceRule: string | null;
  recurrenceId: string | null;
  /** Wall-clock exception dates */
  recurrenceExceptions: Date[] | null;
  resourceId?: string;
  resourceIds?: string[];
  color: string;
  description: string;
  customAttributes: CustomAttributeValue[];
  editable: boolean;
  deletable: boolean;
  clientId?: string;
  isGroupTherapy?: boolean;
  groupCapacity?: number;
  groupClientIds?: string[];
  groupType?: string;
  facilityType?: string;
  location?: string;
  /** Original ScheduleEvent — available to custom item renderers via config.eventIcon */
  _sourceEvent?: ScheduleEvent;
  [key: string]: unknown;
}

const DEFAULT_COLOR = '#3B82F6';

// ─── Our ScheduleEvent (UTC strings) → KendoItem (wall-clock Dates) ───────

export function toKendoItem(event: ScheduleEvent, timezone: string): KendoItem {
  return {
    id: event.id,
    title: event.title,
    start: toZonedTime(new Date(event.startUtc), timezone),
    end: toZonedTime(new Date(event.endUtc), timezone),
    startTimezone: null,
    endTimezone: null,
    isAllDay: event.isAllDay ?? false,
    recurrenceRule: event.recurrenceRule ?? null,
    recurrenceId: event.recurrenceId ?? null,
    recurrenceExceptions: event.recurrenceExceptions
      ? event.recurrenceExceptions.map((s) => toZonedTime(new Date(s), timezone))
      : null,
    resourceId: event.resourceIds?.[0] ?? event.resourceId,
    resourceIds: event.resourceIds?.length ? event.resourceIds : (event.resourceId ? [event.resourceId] : []),
    color: event.backgroundColor ?? event.color ?? DEFAULT_COLOR,
    description: event.description ?? '',
    customAttributes: event.customAttributes ?? [],
    editable: event.editable !== false,
    deletable: event.deletable !== false,
    clientId: event.clientId,
    isGroupTherapy: event.isGroupTherapy,
    groupCapacity: event.groupCapacity,
    groupClientIds: event.groupClientIds,
    groupType: event.groupType,
    facilityType: event.facilityType,
    location: event.location,
    _sourceEvent: event,
  };
}

// ─── KendoItem (wall-clock Dates) → ScheduleEvent (UTC strings) ───────────

export function fromKendoItem(item: KendoItem, timezone: string): ScheduleEvent {
  return {
    id: String(item.id),
    title: item.title ?? '',
    startUtc: fromZonedTime(item.start, timezone).toISOString(),
    endUtc: fromZonedTime(item.end, timezone).toISOString(),
    isAllDay: item.isAllDay,
    recurrenceRule: item.recurrenceRule ?? undefined,
    recurrenceId: item.recurrenceId ?? undefined,
    recurrenceExceptions: item.recurrenceExceptions
      ? item.recurrenceExceptions.map((d) => fromZonedTime(d, timezone).toISOString())
      : undefined,
    resourceId: item.resourceIds?.[0] ?? item.resourceId,
    resourceIds: item.resourceIds?.length ? item.resourceIds : (item.resourceId ? [item.resourceId] : undefined),
    color: item.color,
    backgroundColor: item.color,
    description: item.description,
    customAttributes: item.customAttributes ?? [],
    editable: item.editable,
    deletable: item.deletable,
    clientId: item.clientId,
    isGroupTherapy: item.isGroupTherapy,
    groupCapacity: item.groupCapacity,
    groupClientIds: item.groupClientIds,
    groupType: item.groupType as string | undefined,
    facilityType: item.facilityType as string | undefined,
    location: item.location as string | undefined,
  };
}

// ─── Detect move vs resize from onDataChange ──────────────────────────────

export type ChangeKind = 'moved' | 'resized-start' | 'resized-end' | 'edited';

export function detectChangeKind(original: KendoItem, updated: KendoItem): ChangeKind {
  // Compare durations using wall-clock ISO strings (the tz offset is symmetric
  // so duration arithmetic is stable even without converting back to UTC)
  const origDuration = durationMinutes(
    original.start.toISOString(),
    original.end.toISOString(),
  );
  const newDuration = durationMinutes(
    updated.start.toISOString(),
    updated.end.toISOString(),
  );

  if (origDuration !== newDuration) {
    return original.start.getTime() !== updated.start.getTime()
      ? 'resized-start'
      : 'resized-end';
  }

  const timeMoved =
    original.start.getTime() !== updated.start.getTime() ||
    original.end.getTime() !== updated.end.getTime();
  return timeMoved ? 'moved' : 'edited';
}

// ─── Blank new event (dates are already wall-clock from Kendo slot) ────────

export function blankKendoItem(start: Date, slotDuration: number): KendoItem {
  const end = new Date(start.getTime() + slotDuration * 60 * 1000);
  return {
    id: '',
    title: '',
    start,
    end,
    startTimezone: null,
    endTimezone: null,
    isAllDay: false,
    recurrenceRule: null,
    recurrenceId: null,
    recurrenceExceptions: null,
    resourceIds: [],
    color: DEFAULT_COLOR,
    description: '',
    customAttributes: [],
    editable: true,
    deletable: false,
  };
}
